import type { ReflectionPrompt as BackendReflectionPrompt } from '@/models/preformanceManagement';

/**
 * CO-591 — how a UI card finds its server prompt.
 *
 * The four cards are fixed UI copy (translated locally), but the server prompt ids they post to
 * are NOT constants: `ReflectionService.SeedDefaultPromptsAsync` inserts four rows PER COMPANY
 * with fresh identity ids, and `SaveOrSubmit` only accepts an id that belongs to the caller's
 * company (or a pre-CO-241 global row). The old `{contribution: 1, ...}` map therefore only ever
 * worked on the first tenant seeded; every later tenant got `400 Invalid prompt ID: 1`.
 *
 * Resolution order, against the list returned by `GET /api/Reflection/GetPrompts` (the raw
 * entity, so it carries both attributes):
 *   1. `promptId` — the seeded stable key (`proudest-contribution`, ...). Primary because it names
 *      the prompt, not its position.
 *   2. `displayOrder` 1–4 — fallback for a tenant whose keys were customised/renamed but that
 *      still has the four-slot layout the UI assumes.
 * A company-scoped row wins over a global (`companyId == null`) one when both match, since the
 * company's own row is the one HR can edit. No match => `undefined`, and the card shows an inline
 * "prompts not configured" refusal instead of posting a guessed id.
 */
const UI_PROMPT_BINDINGS: Record<string, { key: string; order: number }> = {
  contribution: { key: 'proudest-contribution', order: 1 },
  support: { key: 'supportive-colleagues', order: 2 },
  growth: { key: 'learning-growth', order: 3 },
  challenges: { key: 'overcoming-challenges', order: 4 },
};

const preferCompanyScoped = (matches: BackendReflectionPrompt[]): BackendReflectionPrompt | undefined =>
  matches.find((p) => p.companyId != null) ?? matches[0];

export function resolveBackendPromptId(uiPromptId: string, prompts: BackendReflectionPrompt[]): number | undefined {
  const binding = UI_PROMPT_BINDINGS[uiPromptId];
  if (!binding || !Array.isArray(prompts) || prompts.length === 0) return undefined;
  const byKey = prompts.filter((p) => typeof p.promptId === 'string' && p.promptId.toLowerCase() === binding.key);
  const hit = preferCompanyScoped(byKey) ?? preferCompanyScoped(prompts.filter((p) => p.displayOrder === binding.order));
  return hit && typeof hit.id === 'number' ? hit.id : undefined;
}

export function resolveUiPromptId(backendPromptId: number, prompts: BackendReflectionPrompt[]): string | undefined {
  const prompt = prompts.find((p) => p.id === backendPromptId);
  if (!prompt) return undefined;
  const entries = Object.entries(UI_PROMPT_BINDINGS);
  const key = typeof prompt.promptId === 'string' ? prompt.promptId.toLowerCase() : '';
  return (
    entries.find(([, b]) => b.key === key)?.[0]
    ?? entries.find(([, b]) => b.order === prompt.displayOrder)?.[0]
  );
}
